/**
 * Cart Freebie Manager - Handles automatic adding, removing, and security enforcement
 * of promotional freebies based on qualifying subtotal threshold.
 */
if (!window.__cartFreebieFailedVariants) {
  window.__cartFreebieFailedVariants = new Set();
}

if (!customElements.get('cart-freebie-manager')) {
  customElements.define('cart-freebie-manager', class CartFreebieManager extends HTMLElement {
    constructor() {
      super();
      this.lastQualifyingTotal = 0;
      this.lastFreebieCount = 0;
      this.lastCheckTime = 0;
    }

    connectedCallback() {
      this.init();
      this.boundCheckFreebie = () => this.checkFreebie();
      document.addEventListener('cart:updated', this.boundCheckFreebie);
      document.addEventListener('cartdrawer:opened', this.boundCheckFreebie);

      // Intercept checkout clicks if cart does not qualify
      this.boundHandleCheckout = (e) => this.handleCheckoutClick(e);
      document.addEventListener('click', this.boundHandleCheckout, true);

      // Listen for window cart events from user interactions
      if (!window.__cartFreebieFetchPatched) {
        window.__cartFreebieFetchPatched = true;
        const originalFetch = window.fetch;
        window.fetch = function(...args) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
          const isCartChange = url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/clear');
          const isInternal = window.__cartFreebieInternalFetch === true;

          return originalFetch.apply(this, args).then(response => {
            if (isCartChange && response.ok && !isInternal) {
              setTimeout(() => {
                const manager = document.querySelector('cart-freebie-manager');
                if (manager && typeof manager.checkFreebie === 'function') {
                  manager.checkFreebie();
                }
              }, 150);
            }
            return response;
          });
        };
      }
    }

    disconnectedCallback() {
      if (this.boundCheckFreebie) {
        document.removeEventListener('cart:updated', this.boundCheckFreebie);
        document.removeEventListener('cartdrawer:opened', this.boundCheckFreebie);
      }
      if (this.boundHandleCheckout) {
        document.removeEventListener('click', this.boundHandleCheckout, true);
      }
    }

    init() {
      setTimeout(() => {
        this.checkFreebie();
      }, 150);
    }

    handleCheckoutClick(e) {
      const checkoutBtn = e.target.closest('button[name="checkout"], .datalayer__checkout__btn, [href*="/checkout"]');
      if (!checkoutBtn) return;

      if (this.lastQualifyingTotal === 0 && this.lastFreebieCount > 0) {
        e.preventDefault();
        e.stopPropagation();
        alert('Promotional free gifts cannot be ordered separately for ৳0. Please add qualifying products to your cart.');
        this.checkFreebie();
      }
    }

    async checkFreebie() {
      // Prevent overlapping concurrent executions across all manager instances
      if (window.__cartFreebieProcessing) return;

      const now = Date.now();
      if (this.lastCheckTime && now - this.lastCheckTime < 250) return;
      this.lastCheckTime = now;

      const enabled = this.dataset.enabled === 'true' || this.dataset.enabled === '1';
      if (!enabled) return;

      const autoAdd = this.dataset.autoAdd === 'true' || this.dataset.autoAdd === '1';

      // Parse Tier 1 settings
      const tier1Enabled = this.dataset.tier1Enabled === 'true' || this.dataset.tier1Enabled === '1';
      const tier1Threshold = parseInt(this.dataset.tier1Threshold, 10) || 0;
      const tier1VariantIds = (this.dataset.tier1VariantIds || '')
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      // Parse Tier 2 settings (backward-compatible with threshold & variantIds)
      const tier2Enabled = this.dataset.tier2Enabled !== undefined
        ? (this.dataset.tier2Enabled === 'true' || this.dataset.tier2Enabled === '1')
        : (this.dataset.enabled === 'true' || this.dataset.enabled === '1');
      const tier2Threshold = parseInt(this.dataset.tier2Threshold || this.dataset.threshold, 10) || 399900;
      const tier2VariantIds = (this.dataset.tier2VariantIds || this.dataset.variantIds || '')
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      const allConfiguredVariantIds = Array.from(new Set([...tier1VariantIds, ...tier2VariantIds]));
      const freebieHandles = (this.dataset.handles || '')
        .split(',')
        .map(h => h.trim().toLowerCase())
        .filter(h => h.length > 0);

      window.__cartFreebieProcessing = true;

      try {
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();

        let qualifyingTotal = 0;
        const currentFreebieItems = [];

        for (const item of cart.items) {
          const itemVariantId = String(item.variant_id || item.id);
          const itemHandle = (item.handle || (item.product && item.product.handle) || '').toLowerCase();
          const isFreebieProp = Boolean(item.properties && (item.properties._freebie === 'true' || item.properties._freebie === true));
          const isFreebieVariant = allConfiguredVariantIds.includes(itemVariantId);
          const isFreebieHandle = freebieHandles.length > 0 && freebieHandles.includes(itemHandle);
          const isZeroPrice = (item.price === 0 || item.final_price === 0);

          if (isFreebieProp || isFreebieVariant || isFreebieHandle || isZeroPrice) {
            currentFreebieItems.push(item);
          } else {
            qualifyingTotal += item.final_line_price;
          }
        }

        this.lastQualifyingTotal = qualifyingTotal;
        this.lastFreebieCount = currentFreebieItems.length;

        // Determine what freebie variants should be in the cart
        const targetVariantCounts = {};

        const isTier1Unlocked = tier1Enabled && qualifyingTotal >= tier1Threshold && qualifyingTotal > 0 && tier1VariantIds.length > 0;
        if (isTier1Unlocked) {
          const t1Variant = tier1VariantIds.find(id => !window.__cartFreebieFailedVariants.has(String(id)));
          if (t1Variant) {
            targetVariantCounts[t1Variant] = (targetVariantCounts[t1Variant] || 0) + 1;
          }
        }

        const isTier2Unlocked = tier2Enabled && qualifyingTotal >= tier2Threshold && qualifyingTotal > 0 && tier2VariantIds.length > 0;
        if (isTier2Unlocked) {
          const t2Variant = tier2VariantIds.find(id => !window.__cartFreebieFailedVariants.has(String(id)));
          if (t2Variant) {
            targetVariantCounts[t2Variant] = (targetVariantCounts[t2Variant] || 0) + 1;
          }
        }

        let didChange = false;
        const updates = {};
        const seenCounts = {};

        // 1. Reconcile existing freebies in cart: keep needed ones, purge unwanted or duplicate ones
        for (const item of currentFreebieItems) {
          const vId = String(item.variant_id);
          const desiredQty = targetVariantCounts[vId] || 0;
          const currentSeen = seenCounts[vId] || 0;

          if (currentSeen < desiredQty) {
            const keepQty = Math.min(item.quantity, desiredQty - currentSeen);
            seenCounts[vId] = currentSeen + keepQty;
            if (item.quantity !== keepQty) {
              updates[item.key] = keepQty;
              didChange = true;
            }
          } else {
            // Freebie not eligible or exceeded desired quantity: remove it
            updates[item.key] = 0;
            didChange = true;
          }
        }

        if (didChange) {
          window.__cartFreebieInternalFetch = true;
          try {
            const updateRes = await fetch(window.routes ? window.routes.cart_update_url : '/cart/update.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ updates })
            });
            if (!updateRes.ok) {
              didChange = false;
            }
          } finally {
            window.__cartFreebieInternalFetch = false;
          }
        }

        // 2. Add missing quantities for unlocked freebies
        if (autoAdd) {
          const addItems = [];
          for (const [vId, desiredQty] of Object.entries(targetVariantCounts)) {
            const haveQty = seenCounts[vId] || 0;
            const missingQty = desiredQty - haveQty;
            if (missingQty > 0 && !window.__cartFreebieFailedVariants.has(String(vId))) {
              addItems.push({
                id: parseInt(vId, 10),
                quantity: missingQty,
                properties: {
                  '_freebie': 'true',
                  'Promo': 'Free Gift'
                }
              });
            }
          }

          if (addItems.length > 0) {
            window.__cartFreebieInternalFetch = true;
            try {
              const addRes = await fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({ items: addItems })
              });

              if (addRes.ok) {
                didChange = true;
              } else {
                addItems.forEach(item => window.__cartFreebieFailedVariants.add(String(item.id)));
                const errData = await addRes.json().catch(() => ({}));
                console.warn('Could not auto-add free gift:', errData.description || errData.message);
              }
            } finally {
              window.__cartFreebieInternalFetch = false;
            }
          }
        }

        if (didChange) {
          this.refreshCart();
        }
      } catch (err) {
        console.warn('Freebie check error:', err);
      } finally {
        window.__cartFreebieProcessing = false;
      }
    }

    refreshCart() {
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }
      this.refreshCartDrawer();
    }

    refreshCartDrawer() {
      const miniCartEl = document.getElementById('mini-cart');
      if (!miniCartEl) return;

      const url = miniCartEl.dataset.url || `${window.routes ? window.routes.root : '/'}?section_id=mini-cart`;
      fetch(url)
        .then(response => {
          if (!response.ok) throw new Error('Network response not ok: ' + response.status);
          return response.text();
        })
        .then(html => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const newContent = parsed.querySelector('.shopify-section') || parsed.querySelector('form.mini-cart');
          // Only update if valid section content was found (NEVER fall back to parsed.body, preventing Cloudflare/error injection)
          if (newContent) {
            miniCartEl.innerHTML = newContent.innerHTML;
          }
          this.updateCartBubbles();
        })
        .catch(err => console.warn('Could not refresh cart drawer:', err));
    }

    async updateCartBubbles() {
      try {
        const res = await fetch('/cart.js');
        if (!res.ok) return;
        const cart = await res.json();
        document.querySelectorAll('.cart-count-bubble, [data-cart-count]').forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch (e) {
        // silent
      }
    }
  });
}
